import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faSlidersH } from "@fortawesome/pro-solid-svg-icons/faSlidersH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import { EntityType, EntityTypesSupportingSavedFilters } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import getAppliedFiltersCount from "@app/component/filtering/utils/getAppliedFiltersCount";
import useView from "@app/component/view/utils/useView";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getSavedFilters } from "@app/store/savedFilters";
import { selectSavedFilter } from "@app/store/savedFilters/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useClearFilters from "@app/util/hook/useClearFilters";

import UpdateFilterModal from "../filterModals/UpdateFilterModal";

import styles from "./Header.module.scss";
import SavedFiltersDrawer from "./SavedFiltersDrawer";

const messages = defineMessages({
  noFiltersTitle: {
    id: "filters.header.title.noFilters",
    defaultMessage: "Select a field to start filtering",
    description: "Filter dropdown's title when no rules are applied",
  },
  savedFilterApplied: {
    id: "filters.header.title.savedFilterApplied",
    defaultMessage: "Saved filter applied",
    description: "Filter dropdown's title when saved filter applied",
  },
  title: {
    id: "filters.header.title.default",
    defaultMessage: "Apply filter rules",
    description: "Default filter dropdown's title",
  },
});

interface Props {
  entityType: Exclude<EntityTypesSupportingSavedFilters, EntityType.PIN>;
  filterCount: number;
  hideSavedFilters?: boolean;
  implicitFilterFields?: IField[];
  isReadOnlyMember: boolean;
  savedFilters: SavedFilter[];
  selectedFilter?: SavedFilter;
  selectSavedFilter: typeof selectSavedFilter;
}

const Header: React.FC<Props> = ({
  entityType,
  filterCount,
  hideSavedFilters,
  implicitFilterFields,
  isReadOnlyMember,
  savedFilters,
  selectedFilter,
  selectSavedFilter,
}) => {
  const intl = useIntl();
  const handleResetAllFilters = useClearFilters(implicitFilterFields);
  const {
    updateViewState,
    viewState: { filter, viewAs },
  } = useView();

  const [paneVisible, showPane, hidePane] = useBoolean();
  const [editedFilter, setEditedFilter] = useState<SavedFilter | undefined>();
  const handleHideEditFilterModal = useCallback(
    () => setEditedFilter(undefined),
    [setEditedFilter]
  );

  const savedFiltersAnalytics = useExtendedAnalytics("Saved Filters", useAnalytics());
  const handleSavedFiltersClick = useCallback(() => {
    savedFiltersAnalytics.clicked([]);
    showPane();
  }, [savedFiltersAnalytics, showPane]);

  const handleSavedFiltersPaneHide = useCallback(() => {
    savedFiltersAnalytics.cancelled(["Close Filter"]);
    hidePane();
  }, [hidePane, savedFiltersAnalytics]);

  const handleFilterEdit = useCallback(
    (value: SavedFilter) => {
      savedFiltersAnalytics.clicked(["Edit Filter"], { filter: value });
      setEditedFilter(value);
    },
    [savedFiltersAnalytics, setEditedFilter]
  );

  const handleSelectSavedFilter = useCallback(
    (savedFilter: SavedFilter) => {
      savedFiltersAnalytics.initiated(["Apply Filter"], { filter: savedFilter });
      updateViewState({ selectedSavedFilterId: savedFilter.id });
      selectSavedFilter({ entityType, savedFilter });
    },
    [entityType, savedFiltersAnalytics, selectSavedFilter, updateViewState]
  );

  return (
    <div className={styles.container}>
      <div>
        <span>
          {selectedFilter
            ? intl.formatMessage(messages.savedFilterApplied)
            : !filterCount
            ? intl.formatMessage(messages.noFiltersTitle)
            : intl.formatMessage(messages.title)}
        </span>
        {!selectedFilter && getAppliedFiltersCount(filter, viewAs, implicitFilterFields) > 0 && (
          <Button onClick={handleResetAllFilters} size="small" type="control">
            {intl.formatMessage({
              id: "filters.header.clear",
              defaultMessage: "Clear all",
              description: "Clear all button in filters dropdown's header",
            })}
          </Button>
        )}
      </div>
      <div>
        {!hideSavedFilters && (
          <Button
            leftIcon={<FontAwesomeIcon icon={faSlidersH} />}
            onClick={handleSavedFiltersClick}
            size="small"
            type="link"
          >
            {intl.formatMessage({
              id: "filters.header.showSavedFilters",
              defaultMessage: "Saved Filters",
              description: "Saved Filters button in filters dropdown's header",
            })}
          </Button>
        )}
      </div>
      <AnalyticsContext.Provider value={savedFiltersAnalytics}>
        <SavedFiltersDrawer
          editable={!isReadOnlyMember}
          filters={savedFilters}
          onEdit={handleFilterEdit}
          onHide={handleSavedFiltersPaneHide}
          onSelect={handleSelectSavedFilter}
          visible={paneVisible}
        />
        <UpdateFilterModal
          entityType={entityType}
          filter={editedFilter}
          onHide={handleHideEditFilterModal}
        />
      </AnalyticsContext.Provider>
    </div>
  );
};

const mapStateToProps = (
  state: RootState,
  ownProps: { entityType: Exclude<EntityTypesSupportingSavedFilters, EntityType.PIN> }
) => ({
  isReadOnlyMember: isCurrentUserReadOnly(state),
  savedFilters: getSavedFilters(state)(ownProps.entityType),
});

const mapDispatchToProps = {
  selectSavedFilter,
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
